import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/apiClient'
import type {
  Item,
  ItemList,
  ItemMoves,
  CreateItemBody,
  UpdateItemBody,
  PurchaseBody,
  AdjustBody,
  ItemImportCommitResult,
  ItemImportOptions,
  ItemImportPreview,
} from '@/types/backend'
import { containerQK } from './useContainers'
import type { TransferBody } from '@/types/backend'

export type ScannedItem = {
  id: string
  name: string
  sku: string | null
  unitPrice: number
  stockOnHand: number
  type: 'PRODUCT' | 'SERVICE'
  imageUrl?: string | null
}

type FindBySkuResponse = {
  item: ScannedItem | null
}

/**Query Keys */
export const itemQK = {
  list: (q?: {
    search?: string
    categoryId?: string
    page?: number
    pageSize?: number
  }) => ['items', q ?? {}] as const,
  one: (id: string) => ['items', id] as const,
  moves: (id: string) => ['items', id, 'moves'] as const,
  importPreview: (fingerprint: string) =>
    ['items', '_import', 'preview', fingerprint] as const,
}

/** IMPORT PREVIEW */
export const usePreviewItemsImportMutation = () => {
  return useMutation({
    mutationFn: async (payload: {
      file: File
      options?: ItemImportOptions
    }) => {
      const fd = new FormData()
      fd.append('file', payload.file)

      const o = payload.options ?? {}

      if (o.delimiter) fd.append('delimiter', o.delimiter)
      if (o.updateIfExists !== undefined)
        fd.append('updateIfExists', String(o.updateIfExists))
      if (o.createCategories !== undefined)
        fd.append('createCategories', String(o.createCategories))
      if (o.mode) fd.append('mode', o.mode)
      if (o.columns) fd.append('columns', JSON.stringify(o.columns))

      const { data } = await apiClient.post<ItemImportPreview>(
        '/items/_import/preview',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return data
    },
    onSuccess: () => {
      // rien d'obligatoire, mais tu peux mettre en cache si tu veux
      // qc.setQueryData(itemQK.importPreview('last'), data)
    },
  })
}

/** IMPORT COMMIT */
export const useCommitItemsImportMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      file: File
      options?: ItemImportOptions
    }) => {
      const fd = new FormData()
      fd.append('file', payload.file)

      const o = payload.options ?? {}
      if (o.delimiter) fd.append('delimiter', o.delimiter)
      if (o.updateIfExists !== undefined)
        fd.append('updateIfExists', String(o.updateIfExists))
      if (o.createCategories !== undefined)
        fd.append('createCategories', String(o.createCategories))
      if (o.mode) fd.append('mode', o.mode)
      if (o.columns) fd.append('columns', JSON.stringify(o.columns))

      const { data } = await apiClient.post<ItemImportCommitResult>(
        '/items/_import/commit',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return data
    },
    onSuccess: () => {
      // ✅ après import: refresh items + catégories (si createCategories)
      qc.invalidateQueries({ queryKey: ['items'] })
      qc.invalidateQueries({ queryKey: ['categories'] })
    },
  })
}

/** LIST (paginée / filtrée) */
export const useItemsQuery = (
  q: {
    search?: string
    categoryId?: string
    page?: number
    pageSize?: number
  } = {},
  enabled = true
) =>
  useQuery({
    queryKey: itemQK.list(q),
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<ItemList>('/items', { params: q })
      return data
    },
  })

export const useTransferStockMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: TransferBody) => {
      const { data } = await apiClient.post<{ ok: true }>(
        `/items/_transfer`,
        payload
      )
      return data
    },
    onSuccess: (_res, payload) => {
      // rafraîchir listes/mouvements
      qc.invalidateQueries({ queryKey: ['items'] })
      qc.invalidateQueries({ queryKey: itemQK.one(payload.itemId) })
      qc.invalidateQueries({ queryKey: itemQK.moves(payload.itemId) })

      // summaries des 2 conteneurs
      qc.invalidateQueries({
        queryKey: containerQK.one(payload.fromContainerId),
      })
      qc.invalidateQueries({
        queryKey: containerQK.one(payload.toContainerId),
      })
    },
  })
}

/**DETAILS */
export const useItemQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: itemQK.one(id),
    enabled: Boolean(id) && enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<Item>(`/items/${id}`)
      return data
    },
  })

/**CREATE */
export const useCreateItemMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateItemBody) => {
      const { data } = await apiClient.post<Item>('/items', payload)
      return data
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['items'] })
      qc.setQueryData(itemQK.one(created.id), created)
    },
  })
}

/**UPDATE */
export const useUpdateItemMutation = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: UpdateItemBody) => {
      const { data } = await apiClient.patch<Item>(`/items/${id}`, payload)
      return data
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['items'] })
      qc.setQueryData(itemQK.one(id), updated)
    },
  })
}

/**DELETE */
export const useDeleteItemMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete<Item>(`/items/${id}`)
      return data
    },
    onSuccess: (_deleted, id) => {
      qc.invalidateQueries({ queryKey: ['items'] })
      qc.removeQueries({ queryKey: itemQK.one(id) })
      qc.removeQueries({ queryKey: itemQK.moves(id) })
    },
  })
}

/**PURCHASE (entrée stock) */
export const usePurchaseStockMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: PurchaseBody) => {
      const { data } = await apiClient.post<{ ok: true }>(
        `/items/_purchase`,
        payload
      )
      return data
    },
    onSuccess: (_res, payload) => {
      qc.invalidateQueries({ queryKey: ['items'] })
      if (payload.itemId) {
        qc.invalidateQueries({ queryKey: itemQK.one(payload.itemId) })
        qc.invalidateQueries({ queryKey: itemQK.moves(payload.itemId) })
      }
    },
  })
}

/**ADJUST (ajustement +/-) */
export const useAdjustStockMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: AdjustBody) => {
      const { data } = await apiClient.post<{ ok: true }>(
        `/items/_adjust`,
        payload
      )
      return data
    },
    onSuccess: (_res, payload) => {
      qc.invalidateQueries({ queryKey: ['items'] })
      if (payload.itemId) {
        qc.invalidateQueries({ queryKey: itemQK.one(payload.itemId) })
        qc.invalidateQueries({ queryKey: itemQK.moves(payload.itemId) })
      }
    },
  })
}

/**MOUVEMENTS d'un article */
export const useItemMovesQueryQ = (id: string, enabled = true) =>
  useQuery({
    queryKey: itemQK.moves(id),
    enabled: Boolean(id) && enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<ItemMoves>(`/items/${id}/moves`)
      return data
    },
  })

/** FIND BY SKU (scanner code-barres) */
export const useFindItemBySkuMutation = () => {
  return useMutation({
    mutationFn: async (sku: string) => {
      const value = String(sku ?? '').trim()
      const { data } = await apiClient.get<FindBySkuResponse>(
        `/items/by-sku/${encodeURIComponent(value)}`
      )
      return data.item
    },
  })
}
